import { forwardRef, useEffect, useState, memo } from 'react'
import * as THREE from 'three'
import { extend } from '@react-three/fiber'
import { shaderMaterial } from '@react-three/drei'
import imageFragmentShader from '@/shaders/imageFragment.frag'
import vertexShader from '@/shaders/vertex.vert'

export type ImageProps = {
    segments?: number
    scale?: [number, number]
    color?: THREE.Color
    map?: THREE.Texture
    imageBounds?: [number, number]
    texture?: THREE.Texture
    url?: string
    position?: [number, number, number]
    uSpeed?: number
    toneMapped?: boolean
}

const ImageMaterialImpl = shaderMaterial(
    {
        color: new THREE.Color('white'),
        scale: [1, 1],
        imageBounds: [1, 1],
        map: null,
        uSpeed: 0
    },
    vertexShader,
    imageFragmentShader
)
extend({ ImageCustomMaterial: ImageMaterialImpl })

declare global {
    namespace JSX {
        interface IntrinsicElements {
            imageCustomMaterial: Pick<
                ImageProps,
                'color' | 'map' | 'scale' | 'imageBounds' | 'uSpeed'
            >
        }
    }
}

const useTextureOptimized = (url?: string) => {
    const [texture, setTexture] = useState<THREE.Texture | null>(null)
    const [isLoaded, setIsLoaded] = useState(false)

    useEffect(() => {
        if (!url) return

        const loader = new THREE.TextureLoader()
        loader.crossOrigin = 'anonymous'

        loader.load(
            url,
            (loadedTexture) => {
                loadedTexture.minFilter = THREE.LinearFilter
                loadedTexture.magFilter = THREE.LinearFilter
                setTexture(loadedTexture)
                setIsLoaded(true)
            },
            undefined,
            () => {
                // エラー時は何もしない
            }
        )

        return () => {
            if (texture) texture.dispose()
        }
    }, [url])

    return { texture, isLoaded }
}

const R3FImageBase = forwardRef<THREE.Mesh, ImageProps>(
    (
        { url, color, segments = 16, scale = [1, 1], position, ...props },
        ref
    ) => {
        const { texture, isLoaded } = useTextureOptimized(url)

        if (!texture && !isLoaded) {
            return (
                <mesh
                    ref={ref}
                    scale={[...scale, 1]}
                    position={position}
                    {...props}
                >
                    <planeGeometry args={[1, 1, 1, segments]} />
                    <meshBasicMaterial
                        color="#f0f0f0"
                        transparent
                        opacity={0.8}
                    />
                </mesh>
            )
        }

        const planeBounds = [scale[0], scale[1]] as [number, number]
        const imageBounds = texture
            ? ([texture.image.width, texture.image.height] as [number, number])
            : ([1, 1] as [number, number])

        const materialProps = {
            color,
            scale: planeBounds,
            imageBounds,
            toneMapped: false
        } as ImageProps

        return (
            <mesh
                ref={ref}
                scale={[...scale, 1]}
                position={position}
                {...props}
            >
                <planeGeometry args={[1, 1, 1, segments]} />
                {isLoaded && texture ? (
                    <imageCustomMaterial {...materialProps} map={texture} />
                ) : (
                    <meshBasicMaterial
                        color="#f0f0f0"
                        transparent
                        opacity={0.8}
                    />
                )}
            </mesh>
        )
    }
)

export const R3FImage = memo(R3FImageBase)
